import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreditCard, Plus, Trash2, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SavedCardsPage() {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/payments/cards");
            setCards(data || []);
        } catch (error) {
            console.warn("[saved-cards] load failed", error?.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const setDefault = async (id) => {
        try {
            await api.post(`/payments/cards/${id}/default`);
            load();
        } catch (error) {
            console.warn("[saved-cards] set default failed", error?.message);
            toast.error("Could not set default");
        }
    };
    const remove = async (id) => {
        try {
            await api.delete(`/payments/cards/${id}`);
            toast.success("Card removed");
            load();
        } catch (error) {
            console.warn("[saved-cards] remove failed", error?.message);
            toast.error("Could not remove card");
        }
    };

    return (
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <h1 className="font-display text-3xl">Payment cards</h1>
                    <p className="text-sm text-muted-foreground">Manage your saved payment methods (mocked for demo).</p>
                </div>
                <Button onClick={() => setOpen(true)} data-testid="add-card-page-button"><Plus className="h-4 w-4 mr-1" /> Add card</Button>
            </div>
            {loading ? <Skeleton className="h-64" /> :
                cards.length === 0 ? (
                    <Card><CardContent className="p-10 text-center">
                        <CreditCard className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="font-medium">No saved cards</p>
                        <p className="text-sm text-muted-foreground">Add a card to enable seamless dining payments.</p>
                    </CardContent></Card>
                ) : (
                    <div className="grid sm:grid-cols-2 gap-4" data-testid="saved-cards-list">
                        {cards.map(c => (
                            <Card key={c.id} data-testid="saved-card-tile" className="overflow-hidden">
                                <CardContent className="p-5">
                                    <div className="flex items-center justify-between">
                                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{c.brand}</div>
                                        {c.is_default && <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--brand-gold))] inline-flex items-center gap-1"><Star className="h-3 w-3 fill-current" /> Default</span>}
                                    </div>
                                    <div className="mt-3 font-display text-2xl tracking-wide">•••• {c.last4}</div>
                                    <div className="text-xs text-muted-foreground mt-1">{c.holder_name} · exp {c.exp_month}/{c.exp_year}</div>
                                    <div className="flex gap-2 mt-4">
                                        {!c.is_default && (
                                            <Button variant="outline" size="sm" onClick={() => setDefault(c.id)} data-testid="set-default-card-button">Set default</Button>
                                        )}
                                        <Button variant="ghost" size="sm" onClick={() => remove(c.id)} data-testid="remove-card-button" className="text-[hsl(var(--destructive))]">
                                            <Trash2 className="h-4 w-4 mr-1" /> Remove
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )
            }
            <AddCardDialog open={open} onOpenChange={setOpen} onAdded={load} />
        </div>
    );
}

function AddCardDialog({ open, onOpenChange, onAdded }) {
    const [name, setName] = useState("");
    const [number, setNumber] = useState("");
    const [exp, setExp] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        if (!name || !number || !exp) { toast.error("Fill all fields"); return; }
        const [mm, yy] = exp.split("/");
        setSubmitting(true);
        try {
            await api.post("/payments/cards", {
                holder_name: name,
                card_number: number.replace(/\s+/g, ""),
                exp_month: parseInt(mm, 10),
                exp_year: parseInt(yy?.length === 2 ? `20${yy}` : yy, 10),
            });
            toast.success("Card added");
            setName(""); setNumber(""); setExp("");
            onOpenChange(false);
            onAdded?.();
        } catch (error) {
            console.warn("[saved-cards] add card failed", error?.message);
            toast.error(error?.response?.data?.detail || "Could not add card");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader><DialogTitle>Add payment card</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Cardholder name</Label>
                        <Input value={name} onChange={e => setName(e.target.value)} data-testid="page-add-card-name" />
                    </div>
                    <div>
                        <Label>Card number</Label>
                        <Input value={number} onChange={e => setNumber(e.target.value)} placeholder="4242 4242 4242 4242" data-testid="page-add-card-number" />
                    </div>
                    <div>
                        <Label>Exp (MM/YY)</Label>
                        <Input value={exp} onChange={e => setExp(e.target.value)} placeholder="12/30" data-testid="page-add-card-exp" />
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-3">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={submitting} data-testid="page-add-card-save">
                        {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
